/**
 * Save a signed PDF where the user chooses (File System Access API when available).
 * Falls back to a programmatic <a download> for browsers without the API.
 */

function sanitizeFilename(name: string): string {
  const trimmed = (name || 'signed-document.pdf').trim();
  // Replace characters invalid in filenames across Windows/macOS/Linux
  // Control characters (0x00-0x1f) and reserved chars: < > : " / \ | ? *
  let base = trimmed;
  // Replace control characters
  for (let i = 0; i <= 0x1f; i++) {
    base = base.replace(new RegExp(String.fromCharCode(i), 'g'), '_');
  }
  // Replace reserved characters
  base = base.replace(/[<>:"\\|?*]/g, '_');
  base = base.replace(/\//g, '_');
  return base.toLowerCase().endsWith('.pdf') ? base : `${base}.pdf`;
}

function triggerAnchorDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

type SavePickerWindow = Window & {
  showSaveFilePicker?: (options?: {
    suggestedName?: string;
    types?: Array<{ description: string; accept: Record<string, string[]> }>;
    startIn?: FileSystemHandle;
  }) => Promise<FileSystemFileHandle>;
};

export interface SavePdfOptions {
  /**
   * When the PDF was opened via the File System Access API, anchor the Save
   * dialog near it. A file handle is valid here — startIn opens its parent folder.
   */
  startInDirectory?: FileSystemHandle | null;
}

export interface SavePdfResult {
  method: 'picker' | 'anchor';
  filename: string;
}

/**
 * Fetch bytes from a tokenized same-origin download URL and save with a native picker when possible.
 */
export async function saveSignedPdfFromUrl(
  downloadUrl: string,
  suggestedFilename: string,
  options: SavePdfOptions = {}
): Promise<SavePdfResult> {
  const filename = sanitizeFilename(suggestedFilename);

  const res = await fetch(downloadUrl);
  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText);
    throw new Error(text || `Download failed (${res.status})`);
  }

  const blob = await res.blob();
  if (!blob.size) {
    throw new Error('Downloaded file is empty.');
  }

  const win = window as SavePickerWindow;
  if (typeof win.showSaveFilePicker === 'function') {
    try {
      const handle = await win.showSaveFilePicker({
        suggestedName: filename,
        types: [
          {
            description: 'PDF document',
            accept: { 'application/pdf': ['.pdf'] },
          },
        ],
        ...(options.startInDirectory ? { startIn: options.startInDirectory } : {}),
      });
      const writable = await handle.createWritable();
      await writable.write(blob);
      await writable.close();
      return { method: 'picker', filename };
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') {
        throw err;
      }
      // Picker failed — fall through to anchor download
    }
  }

  triggerAnchorDownload(blob, filename);
  return { method: 'anchor', filename };
}

export function buildSignedDownloadFilename(
  originalFileName: string | null | undefined,
  serverSuggestedName?: string | null
): string {
  if (serverSuggestedName) {
    return sanitizeFilename(serverSuggestedName);
  }
  const base = (originalFileName || 'document.pdf').trim();
  if (/^signed[-_]/i.test(base)) {
    return sanitizeFilename(base);
  }
  return sanitizeFilename(`signed-${base}`);
}